import React, { useState, useEffect, ChangeEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Form,
  FormGroup,
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
} from 'reactstrap';
import config from 'config';
import { useAlertPlus, useConfirm } from '../../hooks';
import AlertModal from '../../components/AlertModal';
import ConfirmModal from '../../components/ConfirmModal';
import { ApiService } from '../../services';

import './Edit.scss';

interface NoMatriculateData {
  id: number | string;
  nombre: string;
  apellido: string;
  email: string;
  doc: string;
  validated: boolean;
  validated_at: string | null;
  created_at: string | null;
}

const mailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Edit() {
  const { id } = useParams<{ id: string }>();

  const [data, setData] = useState<NoMatriculateData>({
    id: id ?? '',
    nombre: '',
    apellido: '',
    email: '',
    doc: '',
    validated: false,
    validated_at: null,
    created_at: null,
  });

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [alertShow, alertMessage, alertCallback, alert] = useAlertPlus('');
  const [
    confirmShow,
    confirmTitle,
    confirmAction,
    setConfirm,
  ] = useConfirm(false, '', '');

  useEffect(() => {
    ApiService.getOne('no-matriculates/admin', id)
      .then(({ payload }) => {
        if (payload) {
          setData({
            id: payload.id,
            nombre: payload.nombre ?? '',
            apellido: payload.apellido ?? '',
            email: payload.email ?? '',
            doc: payload.doc ?? '',
            validated: !!payload.validated,
            validated_at: payload.validated_at ?? null,
            created_at: payload.created_at ?? null,
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateForm = (): string | null => {
    if (!data.nombre.trim()) return 'El nombre es obligatorio.';
    if (!data.apellido.trim()) return 'El apellido es obligatorio.';
    if (!data.email.trim() || !mailPattern.test(data.email)) {
      return 'El correo electrónico no es válido.';
    }
    if (!data.doc.trim() || !/^\d{1,8}$/.test(data.doc)) {
      return 'El DNI debe ser numérico de hasta 8 dígitos.';
    }
    if (password || passwordConfirmation) {
      if (password !== passwordConfirmation) {
        return 'Las contraseñas no coinciden.';
      }
      if (password.length < 8) {
        return 'La contraseña debe tener al menos 8 caracteres.';
      }
    }
    return null;
  };

  const handleSave = async () => {
    const error = validateForm();
    if (error) {
      alert(error);
      return;
    }

    setIsLoading(true);

    try {
      const payload: Record<string, unknown> = {
        nombre: data.nombre.trim(),
        apellido: data.apellido.trim(),
        email: data.email.trim(),
        doc: data.doc.trim(),
        validated: data.validated,
      };

      if (password) {
        payload.password = password;
      }

      const res = await ApiService.update('no-matriculates/admin', id, payload);

      if (res?.status !== 'ok') {
        alert(res?.message ?? 'Ocurrió un error al guardar los cambios.');
        return;
      }

      setPassword('');
      setPasswordConfirmation('');
      alert('Datos guardados correctamente.');
    } catch (err) {
      console.error(err);
      alert('Ocurrió un error al guardar los cambios.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPasswordEmail = async () => {
    setConfirm(false);
    setIsLoading(true);

    try {
      const res = await fetch(
        `${config.baseUrl}/no-matriculates/admin/${id}/reset-password`,
        {
          method: 'POST',
          headers: {
            Authorization: localStorage.getItem('accessToken') ?? '',
            'Content-Type': 'application/json',
          },
        },
      );
      const json = await res.json();
      if (json?.status !== 'ok') {
        alert(json?.message ?? 'No se pudo enviar el correo de reseteo.');
        return;
      }
      alert('Se envió el correo con el código de reseteo de contraseña.');
    } catch (err) {
      console.error(err);
      alert('No se pudo enviar el correo de reseteo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmation = (action: string) => {
    if (action === 'reset_password_email') {
      handleResetPasswordEmail();
      return;
    }
    setConfirm(false);
  };

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body"></div>
        </Container>
      </div>
      <Container className="mt--7 no-matriculate-edit" fluid>
        <Card className="bg-secondary shadow">
          <CardHeader>
            <Row>
              <Col md="8">
                <h3 className="mb-0">Modificar No Matriculado #{id}</h3>
              </Col>
              <Col md="4" className="text-right">
                <Link to="/admin/no-matriculates">
                  <Button color="primary" size="md">Volver</Button>
                </Link>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Form>
              <Row>
                <Col sm={6}>
                  <FormGroup>
                    <label className="form-control-label">Apellido</label>
                    <Input
                      type="text"
                      name="apellido"
                      value={data.apellido}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col sm={6}>
                  <FormGroup>
                    <label className="form-control-label">Nombre</label>
                    <Input
                      type="text"
                      name="nombre"
                      value={data.nombre}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <FormGroup>
                    <label className="form-control-label">DNI</label>
                    <Input
                      type="text"
                      name="doc"
                      value={data.doc}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      maxLength={8}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col sm={6}>
                  <FormGroup>
                    <label className="form-control-label">Email</label>
                    <Input
                      type="email"
                      name="email"
                      value={data.email}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <FormGroup check>
                    <label className="form-control-label">
                      <Input
                        type="checkbox"
                        name="validated"
                        checked={data.validated}
                        onChange={handleInputChange}
                        disabled={isLoading}
                      />{' '}
                      Email validado
                    </label>
                  </FormGroup>
                </Col>
              </Row>

              <hr className="my-4" />

              <h6 className="heading text-muted mb-4">Contraseña</h6>
              <Row>
                <Col sm={6}>
                  <FormGroup>
                    <label className="form-control-label">Nueva contraseña</label>
                    <Input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      disabled={isLoading}
                      autoComplete="new-password"
                      placeholder="Dejar vacío para no modificar"
                    />
                  </FormGroup>
                </Col>
                <Col sm={6}>
                  <FormGroup>
                    <label className="form-control-label">Confirmar contraseña</label>
                    <Input
                      type="password"
                      value={passwordConfirmation}
                      onChange={e => setPasswordConfirmation(e.target.value)}
                      disabled={isLoading}
                      autoComplete="new-password"
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col sm={12}>
                  <Button
                    color="warning"
                    onClick={() =>
                      setConfirm(
                        true,
                        '¿Enviar correo con código para resetear la contraseña?',
                        'reset_password_email',
                      )
                    }
                    disabled={isLoading}
                  >
                    Resetear contraseña por email
                  </Button>
                </Col>
              </Row>

              <hr className="my-4" />

              <Row>
                <Col className="text-right">
                  <Button color="success" onClick={handleSave} disabled={isLoading}>
                    Guardar
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Container>
      <AlertModal isOpen={alertShow} message={alertMessage} onClose={alertCallback} />
      <ConfirmModal
        isOpen={confirmShow}
        title={confirmTitle}
        action={confirmAction}
        onConfirm={handleConfirmation}
        onClose={() => setConfirm(false)}
      />
    </>
  );
}

export default Edit;
